import {
    Alert,
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import Button from '../../../core-components/theme/components/Button/Button';
import FileUploadComponent from '../../../globalComponents/FileUploadComponent';
import { useLogbookForm } from '../hooks/useLogbookForm';
import { LogbookTemplateEditData } from '../types/loogBookTypes';
import MediaPreviewCard from '../../../core-components/theme/components/MediaCard';
import { useTranslation } from 'react-i18next';

interface LogbookTemplateFormProps {
    onClose: () => void;
    initialData?: LogbookTemplateEditData;
    selectOptions: {
        types: Array<{ value: string; label: string }>;
        categories: Array<{ value: string; label: string }>;
    };
    optionsStatus: {
        loading: boolean;
        error: boolean;
    };
}

const ALLOWED_MIME_TYPES: string[] = ['image/jpeg', 'image/jpg', 'image/png'];

export default function LogbookTemplateForm({
    onClose,
    initialData,
    selectOptions,
    optionsStatus,
}: LogbookTemplateFormProps) {
    const { t } = useTranslation();
    const {
        formState,
        actions,
        mutationError,
        handleInputChange,
        handleFileSelect,
        handleFileRemove,
        handleRotate,
    } = useLogbookForm({ initialData, onClose });

    const { types, categories } = selectOptions;
    const { loading: optionsLoading, error: optionsError } = optionsStatus;

    return (
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ height: '100%' }}>
            {mutationError && (
                <Grid item xs={12}>
                    <Alert severity="error">
                        {mutationError.message || t('logbook.templates.form.submissionError', 'An unexpected error occurred during submission.')}
                    </Alert>
                </Grid>
            )}

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label={t('logbook.templates.form.name', 'Name')}
                    value={formState.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    variant="outlined"
                    placeholder={t('logbook.templates.form.enterTemplateName', 'Enter template name')}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label={t('logbook.templates.form.description', 'Description')}
                    value={formState.description}
                    onChange={e => handleInputChange('description', e.target.value)}
                    variant="outlined"
                    placeholder={t('logbook.templates.form.enterDescription', 'Enter description')}
                    multiline
                    minRows={2}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" error={optionsError} disabled={optionsLoading}>
                    <InputLabel>{t('logbook.templates.form.type', 'Type')}</InputLabel>
                    <Select
                        value={formState.type}
                        onChange={e => handleInputChange('type', e.target.value)}
                        label={t('logbook.templates.form.type', 'Type')}
                    >
                        {types.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" error={optionsError} disabled={optionsLoading}>
                    <InputLabel>{t('logbook.templates.form.category', 'Category')}</InputLabel>
                    <Select
                        value={formState.category}
                        onChange={e => handleInputChange('category', e.target.value)}
                        label={t('logbook.templates.form.category', 'Category')}
                    >
                        {categories.map(({ value, label }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ flex: 1, height: '50%' }}>
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box'
                    }}
                >
                    {formState.previewUrl ? (
                        <MediaPreviewCard
                            url={formState.previewUrl}
                            filename={formState.file?.name || formState.imageFilename || t('logbook.templates.form.uploadedImage', 'Uploaded Image')}
                            fileType={formState.file?.type}
                            onDelete={handleFileRemove}
                            onRotate={handleRotate}
                            enableRotation={!initialData}
                            enableDelete={!initialData}
                        />
                    ) : (
                        <FileUploadComponent
                            accept={ALLOWED_MIME_TYPES.join(',')}
                            maxFileSize={50}
                            multiple={false}
                            onFileClick={handleFileSelect}
                            supportiveText={t('logbook.templates.form.imageSupported', 'Image Supported: JPG, PNG JPEG')}
                            boxProps={{ width: '100%', height: '100%' }}
                        />
                    )}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
                    {actions.map(action => (
                        <Button
                            key={action.text}
                            variant={action.variant as any}
                            color={action.color as any}
                            onClick={action.onClick}
                            disabled={action.disabled}
                        >
                            {action.text}
                        </Button>
                    ))}
                </Box>
            </Grid>
        </Grid>
    );
}
